import { Controller } from "@hotwired/stimulus"
import { useIdle } from "stimulus-use"
import { useHotkeys } from "stimulus-use/hotkeys"
import { get, patch } from "@lib/requests"
import { Turbo } from "@hotwired/turbo-rails"
import { currentUser, showAlert } from "@lib/utils"

function timeString(date) {
  return date.toLocaleTimeString("fr", { hour: "2-digit", minute: "2-digit" })
}

// Connects to data-controller="calendar"
export default class extends Controller {

  static values = { cellHeight: { type: Number, default: 60 } }

  connect() {
    this.boundResizeMove = this.resizeMove.bind(this)
    this.boundResizeEnd = this.resizeEnd.bind(this)

    useHotkeys(this, {
      1: [this.oneDayMode],
      2: [this.twoDayMode],
      4: [this.fourDayMode],
      7: [this.weekMode],
      v: [this.vetMode],
      "left": [this.previousDate],
      "right": [this.nextDate]
    })
  }

  disconnect() {
    clearInterval(this.interval)
    window.removeEventListener("mousemove", this.boundResizeMove)
    window.removeEventListener("mouseup", this.boundResizeEnd)
  }

  get scrollContainer() {
    return this.element.querySelector(".calendar-scrollable-container")
  }

  // JS Workaround as the "Déplacer" link is not reliably getting the correct date from the URL (turbo related?)
  // We are setting the pinned consultation and updating the link to the true reflection of the URL
  move(e) {
    e.preventDefault()
    const link = e.currentTarget
    const url = new URL(window.location)
    url.searchParams.set("pinned", link.dataset.pinned)
    link.href = url
    link.removeAttribute("data-action")
    setTimeout(() => {
      link.click()
    }, 0)
  }

  // Referencing the event block that is being dragged
  dragStart(e) {
    if (this.isResizeHandle(e.target)) {
      e.preventDefault()
      return
    }

    e.dataTransfer.setData("text/plain", e.currentTarget.dataset.id)
    e.target.classList.add("in-transition")
    this.draggedEvent = e.currentTarget
  }

  // Referencing the time slot the block is being dragged over
  dragOver(e) {
    e.preventDefault()
  }

  validateDrag(slot) {
    const presenceIdsJson = slot.dataset.presenceIds || slot.closest(".calendar-column")?.dataset?.presenceIds
    const presenceIds = presenceIdsJson ? JSON.parse(presenceIdsJson) : []
    const slotVetId = slot.dataset.vet ? parseInt(slot.dataset.vet, 10) : null
    const eventVetId = this.draggedEvent.dataset.vet ? parseInt(this.draggedEvent.dataset.vet, 10) : null
    const validSameDateDrop = new Date(slot.dataset.date).toDateString() === new Date(this.draggedEvent.dataset.date).toDateString()
    const validResourceDrop = slotVetId && presenceIds.includes(slotVetId) || false
    const validDateDrop = !validResourceDrop && ((presenceIds.length === 1) || presenceIds.includes(eventVetId))
    const validDrop = validSameDateDrop || validResourceDrop || validDateDrop
    const defaultVetId = validDateDrop ? presenceIds.length == 1 ? presenceIds[0] : eventVetId : null
    const vetId = validResourceDrop ? slotVetId : defaultVetId
    return { validDrop, validSameDateDrop, validResourceDrop, validDateDrop, vetId, presenceIds }
  }

  // Referencing the time slot the block is being entered
  dragEnter(e) {
    const { validDrop } = this.validateDrag(e.currentTarget)
    const bgClass = validDrop ? "bg-yellow-100/60" : "bg-red-100/60"
    e.target.classList.add(bgClass)
  }

  // Referencing the time slot the block is being exited
  dragLeave(e) {
    e.target.classList.remove("bg-yellow-100/60")
    e.target.classList.remove("bg-red-100/60")
  }

  // Referencing the element that is being dragged
  dragEnd(e) {
    e.target.classList.remove("in-transition")
    this.draggedEvent = null
  }

  // The drop event which targets the time slot the block is dropped on
  async drop(e) {
    e.preventDefault()

    const eventType = this.draggedEvent.dataset.type

    if (!["consultation", "blocked_event"].includes(eventType)) return console.error("Invalid event type being dropped")

    const id = e.dataTransfer.getData("text/plain")
    const slot = e.currentTarget

    const date = slot.dataset.date
    const draggedDate = new Date(this.draggedEvent.dataset.date)

    // Remove the yellow or red background from the time slot
    e.target.classList.remove("bg-yellow-100/60")
    e.target.classList.remove("bg-red-100/60")

    // Add a check here to see if the drop is valid, also extract validDateDrop
    const { validDrop, vetId } = this.validateDrag(slot)

    // Return here unless this is a valid drop
    if (!validDrop) return

    const duration = this.draggedEvent.dataset.duration || 30
    const startTime = slot.dataset.slotStart ? new Date(slot.dataset.slotStart) : null
    const endTime = startTime ? new Date(startTime.getTime() + duration * 60000) : null
    const body = { date, start_time: startTime.toISOString(), end_time: endTime.toISOString(), vet_id: vetId }

    // Optimistic UI: immediately reposition the event to the target slot
    const targetColumn = slot.closest(".calendar-column")
    const currentColumn = this.draggedEvent.closest(".calendar-column")
    if (targetColumn && startTime) {
      // Move to new column if different (cross-date or cross-vet drop)
      if (targetColumn !== currentColumn) {
        // Update colors to match the target vet (grab from an existing event in the column)
        const siblingEvent = targetColumn.querySelector(".calendar-event")
        if (siblingEvent) {
          const swapClass = (el, sibEl, prefix) => {
            const newCls = [...sibEl.classList].find(c => c.startsWith(prefix) && !c.includes("gray"))
            const oldCls = [...el.classList].find(c => c.startsWith(prefix) && !c.includes("gray"))
            if (newCls && oldCls && newCls !== oldCls) { el.classList.remove(oldCls); el.classList.add(newCls) }
          }
          // Event bg and border
          swapClass(this.draggedEvent, siblingEvent, "bg-")
          swapClass(this.draggedEvent, siblingEvent, "border-")
          // Resize handle bg
          const handle = this.draggedEvent.querySelector("[data-action*='resizeStart']")
          const sibHandle = siblingEvent.querySelector("[data-action*='resizeStart']")
          if (handle && sibHandle) swapClass(handle, sibHandle, "bg-")
        }
        targetColumn.appendChild(this.draggedEvent)
      }
      // Set new vertical position based on slot offset and update the time display
      this.draggedEvent.style.top = `${slot.offsetTop}px`
      this.draggedEvent.style.left = "0%"
      this.draggedEvent.style.width = "98%"
      this.draggedEvent.dataset.date = date
      this.draggedEvent.dataset.startTime = startTime.toISOString()
      this.draggedEvent.dataset.endTime = endTime.toISOString()
      const timeEl = this.draggedEvent.querySelector(".event-time")
      if (timeEl) timeEl.textContent = [timeString(startTime), timeString(endTime)].join(" - ")
    }

    await patch(`/${eventType}s/${id}/move`, { body, responseKind: "turbo-stream" })
  }

  resizeStart(e) {
    e.preventDefault();
    e.stopPropagation();

    // Assistants can't change the duration of a consultation with a category
    if (e.target.closest("[data-calendar-resize-handle]")?.dataset.durationLocked === "true" && currentUser()?.assistant) {
      return showAlert("La durée d'une consultation avec catégorie ne peut pas être modifiée.", { type: "error" })
    }

    // Set the event we are resizing
    this.resizedEvent = e.target.closest(".calendar-event");
    this.resizedEventWasDraggable = this.resizedEvent.getAttribute("draggable");
    this.resizedEvent.setAttribute("draggable", "false");
    this.eventEndTime = new Date(this.resizedEvent.dataset.endTime);

    // Capture the starting mouse Y position and the current height of the event box
    this.startY = e.clientY;
    this.startHeight = this.resizedEvent.offsetHeight;

    // Find the column's end time to calculate the max height of the event, 10 minutes = 60px
    const column = this.resizedEvent.closest(".calendar-column");
    const columnEndTime = new Date(column.dataset.columnEndTime);
    const eventStartTime = new Date(this.resizedEvent.dataset.startTime);
    const maxEventDuration = columnEndTime - eventStartTime;
    const maxEventDurationInMinutes = maxEventDuration / 60000;

    // Set the max height of the event box
    this.maxEventHeight = maxEventDurationInMinutes * 6;

    window.addEventListener("mousemove", this.boundResizeMove)
    window.addEventListener("mouseup", this.boundResizeEnd)

  }

  // TODO: Determine if we need to throttle this function
  resizeMove(e) {
    if (!this.resizedEvent) return;

    // Calculate how much the mouse has moved vertically
    const deltaY = e.clientY - this.startY;

    // Update the height based on the initial height + the mouse movement
    const newHeight = this.startHeight + deltaY;

    // Ensure the height is within the min and max bounds
    const safeHeight = Math.max(this.cellHeightValue, Math.min(newHeight, this.maxEventHeight));

    // Apply the new height to the element
    this.resizedEvent.style.height = `${safeHeight}px`;

    // Determine the new duration of the event
    const newDuration = Math.round(safeHeight / this.cellHeightValue) * 10;

    // Determine the event start time
    const eventStartTime = new Date(this.resizedEvent.dataset.startTime);

    // Calculate and set the new end time
    this.eventEndTime = new Date(eventStartTime.getTime() + newDuration * 60000);

    // Get the current time string element
    const timeStringElement = this.resizedEvent.querySelector(".event-time")

    // Store current time string in case we need to revert
    const currentTimeString = timeStringElement.textContent;

    // Set the new time string eg: "09:45 - 10:30"
    const newTimeString = [timeString(eventStartTime), timeString(this.eventEndTime)].join(" - ");

    // Set the new time string in the element
    timeStringElement.textContent = newTimeString;
  }

  async resizeEnd(e) {
    if (!this.resizedEvent) return;

    window.removeEventListener("mousemove", this.boundResizeMove)
    window.removeEventListener("mouseup", this.boundResizeEnd)

    // As this event is running on the window object (set in data-action, so that the resizing can continue even if the mouse leaves the event box)
    // we need to stop the event from propagating to the window object (calls twice otherwise)
    e.stopImmediatePropagation();

    const eventType = this.resizedEvent.dataset.type;

    if (!["consultation", "blocked_event"].includes(eventType)) return console.error("Invalid event type being resized")

    // Snap height to 10-min grid on release
    const currentHeight = this.resizedEvent.offsetHeight;
    const snappedHeight = Math.max(this.cellHeightValue, Math.round(currentHeight / this.cellHeightValue) * this.cellHeightValue);
    this.resizedEvent.style.height = `${snappedHeight - 4}px`;

    // Recalculate end time from snapped duration
    const snappedDuration = (snappedHeight / this.cellHeightValue) * 10;
    const eventStartTime = new Date(this.resizedEvent.dataset.startTime);
    this.eventEndTime = new Date(eventStartTime.getTime() + snappedDuration * 60000);
    this.resizedEvent.dataset.endTime = this.eventEndTime.toISOString()

    // Update the time string to match snapped time
    const timeEl = this.resizedEvent.querySelector(".event-time")
    if (timeEl) timeEl.textContent = [timeString(eventStartTime), timeString(this.eventEndTime)].join(" - ")

    // Set the body data to send to the server
    const body = { end_time: this.eventEndTime.toISOString() };

    // Set the event id
    const id = this.resizedEvent.dataset.id;

    // Send a request to the server to update the event's end time
    await patch(`/${eventType}s/${id}/resize`, { body, responseKind: "turbo-stream" })

    // Allow a small delay before resetting the resized event parameters
    setTimeout(() => {
      if (this.resizedEventWasDraggable === null) {
        this.resizedEvent.removeAttribute("draggable");
      } else {
        this.resizedEvent.setAttribute("draggable", this.resizedEventWasDraggable);
      }

      this.resizedEvent = null;
      this.resizedEventWasDraggable = null;
      this.startY = 0;
      this.startHeight = 0;
      this.maxEventHeight = 0;
      this.eventEndTime = null;
    }, 20);

  }

  eventClick(e) {
    if (e.target.closest("a")) return
    if (this.isResizeHandle(e.target)) return

    e.preventDefault()
    if (this.resizedEvent) return

    const url = e.currentTarget.href || e.currentTarget.dataset.url
    if (!url) return

    get(url, { responseKind: "turbo-stream" })
  }

  isResizeHandle(element) {
    return element.closest("[data-calendar-resize-handle]")
  }

  slotDragStart(e) {
    e.preventDefault();
    // Start from a clean selection: a previous drag may not have ended on a slot
    this.clearSlotSelection();
    this.selectedSlot = e.currentTarget.closest(".calendar-cell");
    this.selectedColumn = this.selectedSlot.closest(".calendar-column");
    // A drag released anywhere other than a slot never reaches slotDragEnd, so cancel it here.
    // Otherwise its times linger and the next click on a slot opens a second consultation form.
    window.addEventListener("mouseup", (e) => {
      if (!e.target.closest?.("[data-action*='calendar#slotDragEnd']")) this.clearSlotSelection();
    }, { once: true });
  }

  clearSlotSelection() {
    this.selectedColumn?.querySelectorAll(".calendar-cell").forEach(cell => {
      cell.classList.remove("selected");
      cell.textContent = "";
    });
    this.selectedSlot = null;
    this.selectedColumn = null;
    this.date = null;
    this.startTime = null;
    this.endTime = null;
  }

  slotDragMove(e) {

    // If there is no selected slot, we are not dragging
    if (!this.selectedSlot) return;

    // Identify the cell being dragged over
    const cell = e.currentTarget.closest(".calendar-cell");

    // Get all slots inside the column
    const columnCells = this.selectedColumn.querySelectorAll(".calendar-cell");

    // Remove the highlight from all slots
    columnCells.forEach(cell => cell.classList.remove("selected"));

    // Set the slots as date values
    const selectedSlotStart = new Date(this.selectedSlot.dataset.slotStart)
    const cellStart = new Date(cell.dataset.slotStart)

    // Set the date
    this.date = new Date(this.selectedSlot.dataset.date);

    // Determine if the current cell is before or after the selected ("anchored") slot
    if (cellStart > selectedSlotStart) {
      this.startTime = new Date(this.selectedSlot.dataset.slotStart)
      this.endTime = new Date(cell.dataset.slotEnd)
    } else if (cellStart < selectedSlotStart) {
      this.startTime = new Date(cell.dataset.slotStart)
      this.endTime = new Date(this.selectedSlot.dataset.slotEnd)
    } else {
      this.startTime = new Date(this.selectedSlot.dataset.slotStart)
      this.endTime = new Date(this.selectedSlot.dataset.slotEnd)
    }

    // Filter the slots that fall between the start and end times
    const cells = Array.from(columnCells).filter(cell => new Date(cell.dataset.slotStart) >= this.startTime && new Date(cell.dataset.slotEnd) <= this.endTime);

    // Add the highlight to the slots between the start and end slot
    cells.forEach(cell => cell.classList.add("selected"));

    // Create a time string eg: "09:45 - 10:30"
    const time = [timeString(this.startTime), timeString(this.endTime)].join(" - ");

    // Clear all text content from the slots
    columnCells.forEach(cell => cell.textContent = "");

    // Add it as text content to the first selected slot
    this.selectedColumn.querySelector(".calendar-cell.selected").textContent = time;

  }

  async slotDragEnd(e) {

    if (!this.startTime && !this.endTime) {
      this.selectedSlot = null;
      this.selectedColumn = null;
      const cell = e.currentTarget.closest(".calendar-cell");
      cell.classList.remove("selected");
      return
    }

    // Set the date/times to ISO strings
    const date = this.date.toISOString().split("T")[0];
    const startTime = this.startTime.toISOString();
    const endTime = this.endTime.toISOString();

    // Also set the clinic and potentially the vet
    const clinicId = this.selectedColumn.dataset.clinicId;
    const vetId = this.selectedSlot.dataset.vet;
    const clientId = this.selectedSlot.dataset.clientId;
    const animalId = this.selectedSlot.dataset.animalId;

    // Create the base URL string
    let urlString = `/consultations/new?date=${date}&start_time=${startTime}&end_time=${endTime}&clinic_id=${clinicId}&mode=calendar`;

    // Optionally add the vet, client and animal ids
    urlString += vetId ? `&vet_id=${vetId}` : "";
    urlString += clientId ? `&client_id=${clientId}` : "";
    urlString += animalId ? `&animal_ids[]=${animalId}` : "";

    await get(urlString, { responseKind: "turbo-stream" })

    // Clear the selections and time string
    setTimeout(() => this.clearSlotSelection(), 20);
  }

  visitURL(id) {
    const element = document.getElementById(id)
    element && element.click()
  }

  // NOTE: Finding the relevant element on the page and clicking it seems to set/unset the progress more reliably than using Turbo.visit
  vetMode() {
    this.visitURL("calendar-mode-vets")
  }

  oneDayMode() {
    this.visitURL("calendar-mode-one-day")
  }

  twoDayMode() {
    this.visitURL("calendar-mode-two-days")
  }

  fourDayMode() {
    this.visitURL("calendar-mode-four-days")
  }

  weekMode() {
    this.visitURL("calendar-mode-week")
  }

  previousDate() {
    // If we have a modal open (look for .modal-bg), we don't want to navigate the date
    if (document.querySelector(".modal-bg")) return
    this.visitURL("calendar-previous-date")
  }

  nextDate() {
    // If we have a modal open (look for .modal-bg), we don't want to navigate the date
    if (document.querySelector(".modal-bg")) return
    this.visitURL("calendar-next-date")
  }

  setLinkParams() {
    const params = getSafeParams(["date", "days", "view", "rm"])

    const dropdown = document.getElementById("calendar-clinic-dropdown")
    const items = dropdown.querySelectorAll("a.menu-item")
    items.forEach(item => {
      const base = item.href.split("?")[0];
      const url = new URL(base);
      url.search = new URLSearchParams(params).toString();
      item.href = url.toString();
    })
  }

  unpin() {
    // Visit the current URL but with the pinned param removed
    const url = new URL(window.location.href);
    url.searchParams.delete("pinned");
    Turbo.visit(url.toString());
  }

  unpinAfterMove(e) {
    if (e.detail.success === false) return

    const url = new URL(window.location.href)
    if (!url.searchParams.has("pinned")) return

    url.searchParams.delete("pinned")
    Turbo.visit(url.toString(), { action: "replace" })
  }

}
